<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid class="pt-0">
      <h2 class="mb-4">{{ $t('dueDiligence.ddsReportExchange') }}</h2>

      <v-card elevation="0" class="pb-4 pl-4">
        <v-tabs v-model="activeTab" background-color="transparent" class="exchange-tabs">
          <v-tab class="text-capitalize font-weight-bold">
            {{ $t('dueDiligence.importDdsReport') }}
          </v-tab>
          <v-tab class="text-capitalize font-weight-bold">
            {{ $t('dueDiligence.exchangeRequests') }}
          </v-tab>
        </v-tabs>
      </v-card>
      <v-tabs-items v-model="activeTab" class="black--text">
        <!-- Import DDS Report tab -->
        <v-tab-item>
          <v-card elevation="0" class="mt-4">
            <div class="px-4 py-6">
              <p class="text-body-2 mb-4 grey--text text--darken-1">
                {{ $t('dueDiligence.importDdsReportDescription') }}
              </p>

              <p class="font-weight-medium mb-2">{{ $t('dueDiligence.selectMethodOfImport') }}</p>
              <v-radio-group v-model="importMethod" hide-details class="mt-0 mb-4">
                <v-radio :label="$t('dueDiligence.requestInternalRefNumber')" value="internal-ref"
                  color="primary"></v-radio>
                <v-radio :label="$t('dueDiligence.iHaveAccessCode')" value="access-code" color="primary"></v-radio>
              </v-radio-group>

              <v-row v-if="importMethod === 'access-code'" class="mb-4">
                <v-col cols="6">
                  <label class="d-flex align-center mb-2">
                    {{ $t('dueDiligence.accessCode') }}
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ml-1" color="primary" v-bind="attrs" v-on="on">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t('dueDiligence.accessCodeTooltip') }}</span>
                    </v-tooltip>
                  </label>
                  <v-text-field width="50%" v-model="accessCode" :placeholder="$t('dueDiligence.enterHere')" outlined
                    dense hide-details clearable class="access-code-input"></v-text-field>
                </v-col>
              </v-row>

              <v-row v-if="importMethod === 'internal-ref'" class="mb-4">
                <v-col cols="6">
                  <label class="d-flex align-center mb-2">
                    {{ $t('dueDiligence.internalReferenceNumber') }}
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ml-1" color="primary" v-bind="attrs" v-on="on">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t('dueDiligence.internalRefRequestTooltip') }}</span>
                    </v-tooltip>
                  </label>
                  <v-text-field v-model="internalRefNumber" :placeholder="$t('dueDiligence.enterHere')" outlined dense
                    hide-details clearable></v-text-field>
                </v-col>
              </v-row>

              <v-btn color="primary" class="text-none" :disabled="!canAccessReport || accessReportLoading"
                :loading="accessReportLoading" @click="accessTheReport">
                {{ importMethod === 'access-code' ? $t('dueDiligence.accessTheReport') : $t('SEND REQUEST') }}
              </v-btn>
            </div>
          </v-card>

          <!-- Exchange History (shown on both tabs per design) -->
          <v-card elevation="0" class="mt-6">
            <v-card-text>
              <h3 class="font-weight-bold mb-2">{{ $t('dueDiligence.exchangeHistory') }}</h3>
              <p class="text-body-2 mb-4 grey--text text--darken-1">
                {{ $t('dueDiligence.exchangeHistoryDescription') }}
              </p>

              <v-data-table :headers="tableHeaders" :items="exchangeHistoryItems"
                :items-per-page="reportOptions.itemsPerPage" :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                item-key="id" hide-default-footer class="elevation-0 exchange-table" :loading="tableLoading"
                :options.sync="reportOptions" :loading-text="$t('loadingData')"
                :item-class="getExchangeHistoryRowClass" @click:row="onExchangeHistoryRowClick">
                <template v-slot:top>
                  <div class="table-controls-container">
                    <div class="filters-row mb-2">
                      <v-row>
                        <v-col cols="2">
                          <div class="control search">
                            <label for="exchange-request-id">&nbsp;</label>
                            <v-text-field id="exchange-request-id" v-model="filters.requestId"
                              prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                              :placeholder="$t('dueDiligence.requestId')" @input="onFilterChange" />
                          </div>
                        </v-col>
                        <v-col cols="2">
                          <div class="control filter">
                            <label for="exchange-enterprise">{{ $t('dueDiligence.enterprise') }}</label>
                            <v-text-field id="exchange-enterprise" v-model="filters.enterprise"
                              prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                              :placeholder="$t('search')" @input="onFilterChange" />
                          </div>
                        </v-col>
                        <v-col cols="2">
                          <div class="control filter">
                            <label for="exchange-import-method">{{ $t('dueDiligence.importMethod') }}</label>
                            <v-select id="exchange-import-method" v-model="filters.importMethod"
                              :items="importMethodOptions" outlined dense hide-details clearable
                              :placeholder="$t('dueDiligence.all')" @change="onFilterChange" />
                          </div>
                        </v-col>
                        <v-col cols="2">
                          <div class="control filter">
                            <label for="exchange-request-status">{{ $t('dueDiligence.requestStatus') }}</label>
                            <v-select id="exchange-request-status" v-model="filters.requestStatus"
                              :items="requestStatusOptions" outlined dense hide-details clearable
                              :placeholder="$t('dueDiligence.all')" @change="onFilterChange" />
                          </div>
                        </v-col>
                        <v-spacer></v-spacer>
                        <div class="buttons-row d-flex align-center">
                          <div class="pagination-controls mr-4">
                            <span class="pagination-label">{{ fromToLabel }} {{ $t('of') }} {{ totalCount }}</span>
                            <v-btn fab small outlined color="primary" :disabled="reportOptions.page <= 1" class="ml-2 mr-2"
                              @click="reportPageChange(false)">
                              <v-icon dark>mdi-chevron-left</v-icon>
                            </v-btn>
                            <v-btn fab small outlined color="primary" :disabled="reportOptions.page >= totalPages"
                              @click="reportPageChange(true)">
                              <v-icon dark>mdi-chevron-right</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </v-row>
                    </div>
                  </div>
                </template>
                <template v-slot:progress>
                  <v-progress-linear color="primary" :height="6" indeterminate></v-progress-linear>
                </template>
                <template v-slot:no-data>
                  <span class="text-center">{{ $t('noDataAvailable') }}</span>
                </template>
                <template v-slot:item.requestId="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.requestId }}</div>
                    <div class="text-caption grey--text">{{ item.requestDate }}</div>
                  </div>
                </template>
                <template v-slot:item.enterprise="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.enterpriseName }}</div>
                    <div class="text-caption grey--text">{{ item.enterpriseId }}</div>
                  </div>
                </template>
                <template v-slot:item.importMethod="{ item }">
                  <div>
                    <div>{{ item.importMethodLabel }}</div>
                    <a v-if="item.accessCodeDisplay" href="#"
                      class="text-caption primary--text text-decoration-underline"
                      @click.stop.prevent="copyAccessCode(item)">
                      {{ item.accessCodeDisplay }}
                    </a>
                  </div>
                </template>
                <template v-slot:item.productQuantity="{ item }">
                  <div>
                    <div>{{ item.productName }}</div>
                    <div class="text-caption grey--text">{{ item.quantity }}</div>
                  </div>
                </template>
                <template v-slot:item.dueDiligenceStatus="{ item }">
                  <v-btn small rounded min-width="160" depressed height="34"
                    :class="getDueDiligenceStatusConfig(item.dueDiligenceStatus).cssClass">
                    {{ getDueDiligenceStatusConfig(item.dueDiligenceStatus).label }}
                  </v-btn>
                </template>
                <template v-slot:item.requestStatus="{ item }">
                  <v-btn small rounded min-width="160" depressed height="34"
                    :class="getRequestStatusBtnClass(item.requestStatus)">
                    {{ item.requestStatusLabel || '—' }}
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Exchange Requests tab - Incoming Report Requests -->
        <v-tab-item>
          <v-card elevation="0" class="mt-4">
            <div class="px-4 py-6">
              <div class="d-flex align-center mb-2">
                <h3 class="font-weight-bold mr-2">{{ $t('dueDiligence.incomingReportRequests') }}</h3>
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span>{{ $t('dueDiligence.incomingReportRequestsTooltip') }}</span>
                </v-tooltip>
              </div>
              <p class="text-body-2 mb-4 grey--text text--darken-1">
                {{ $t('dueDiligence.incomingReportRequestsDescription') }}
              </p>

              <v-data-table :headers="incomingTableHeaders" :items="incomingRequestItems"
                :items-per-page="incomingReportOptions.itemsPerPage"
                :footer-props="{ 'items-per-page-options': [10, 25, 50] }" item-key="id" hide-default-footer
                class="elevation-0 exchange-table incoming-requests-table" :loading="incomingTableLoading"
                :options.sync="incomingReportOptions" :loading-text="$t('loadingData')">
                <template v-slot:top>
                  <div class="table-controls-container">
                    <div class="filters-row mb-2">
                      <v-row>
                        <v-col cols="2">
                          <div class="control search">
                            <label for="incoming-request-id">&nbsp;</label>
                            <v-text-field id="incoming-request-id" v-model="incomingFilters.requestId"
                              prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                              :placeholder="$t('search')" @input="onIncomingFilterChange" />
                          </div>
                        </v-col>
                        <v-col cols="2">
                          <div class="control filter">
                            <label for="incoming-enterprise">{{ $t('dueDiligence.enterprise') }}</label>
                            <v-text-field id="incoming-enterprise" v-model="incomingFilters.enterprise"
                              prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                              :placeholder="$t('search')" @input="onIncomingFilterChange" />
                          </div>
                        </v-col>
                        <v-col cols="2">
                          <div class="control filter">
                            <label class="d-flex align-center">
                              {{ $t('dueDiligence.product') }}
                              <v-tooltip bottom max-width="250">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon small class="ml-1" color="primary" v-bind="attrs"
                                    v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <span>{{ $t('dueDiligence.productFilterTooltip') }}</span>
                              </v-tooltip>
                            </label>
                            <v-select id="incoming-product" v-model="incomingFilters.product" :items="productOptions"
                              outlined dense hide-details clearable :placeholder="$t('dueDiligence.all')"
                              @change="onIncomingFilterChange" />
                          </div>
                        </v-col>

                        <v-spacer></v-spacer>
                        <div class="buttons-row d-flex align-center">
                          <div class="pagination-controls mr-4">
                            <span class="pagination-label">{{ incomingFromToLabel }} {{ $t('of') }} {{ incomingTotalCount }}</span>
                            <v-btn fab small outlined color="primary" :disabled="incomingReportOptions.page <= 1" class="ml-2 mr-2"
                              @click="incomingPageChange(false)">
                              <v-icon dark>mdi-chevron-left</v-icon>
                            </v-btn>
                            <v-btn fab small outlined color="primary" :disabled="incomingReportOptions.page >= incomingTotalPages"
                              @click="incomingPageChange(true)">
                              <v-icon dark>mdi-chevron-right</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </v-row>
                    </div>
                  </div>
                </template>
                <template v-slot:progress>
                  <v-progress-linear color="primary" :height="6" indeterminate></v-progress-linear>
                </template>
                <template v-slot:no-data>
                  <span class="text-center">{{ $t('noDataAvailable') }}</span>
                </template>
                <template v-slot:item.requestId="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.requestId }}</div>
                    <div class="text-caption grey--text">{{ item.requestDate }}</div>
                  </div>
                </template>
                <template v-slot:item.enterprise="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.enterpriseName }}</div>
                    <div class="text-caption grey--text">{{ item.enterpriseId }}</div>
                  </div>
                </template>
                <template v-slot:item.internalRefNo="{ item }">
                  {{ item.internalRefNo || '-' }}
                </template>
                <template v-slot:item.productQuantity="{ item }">
                  <div>
                    <div>{{ item.productName }}</div>
                    <div class="text-caption grey--text">{{ item.quantity }}</div>
                  </div>
                </template>
                <template v-slot:item.dueDiligenceStatus="{ item }">
                  <v-btn small rounded min-width="160" depressed height="34"
                    :class="getDueDiligenceStatusConfig(item.dueDiligenceStatus).cssClass">
                    {{ getDueDiligenceStatusConfig(item.dueDiligenceStatus).label }}
                  </v-btn>
                </template>
                <template v-slot:item.requestStatus="{ item }">
                  <template v-if="item.requestStatus === 'pending' || item.requestStatus === 'request'">
                    <v-btn small color="primary" class="mr-1 text-none" @click="handleApproveRequest(item)">
                      {{ $t('dueDiligence.approve') }}
                    </v-btn>
                    <v-btn small color="error" class="text-none" @click="handleRejectRequest(item)">
                      {{ $t('dueDiligence.reject') }}
                    </v-btn>
                  </template>
                  <v-btn v-else small rounded min-width="160" depressed height="34" :color="getRequestStatusColor(item.requestStatus)" >
                    {{ item.requestStatusLabel || '—' }}
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </v-card>

        </v-tab-item>
      </v-tabs-items>

    </v-container>

    <ImportDialog :value="showImportDialog" @input="showImportDialog = $event" @cancel="onImportCancel"
      @run-in-background="onImportRunInBackground" />
  </div>
</template>

<script>
import DeforestationService from '@/_services/DeforestationService';
import ImportDialog from './ImportDialog.vue';
import BlendService from '@/_services/BlendService';
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
  name: 'DdsReportExchange',
  components: { ImportDialog },
  data() {
    return {
      activeTab: 0,
      importMethod: 'access-code',
      accessCode: '',
      internalRefNumber: '',
      tableLoading: false,
      totalCount: 0,
      accessReportLoading: false,
      showImportDialog: false,
      importRunInBackground: false,
      importCancelled: false,
      reportOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      filters: {
        requestId: '',
        enterprise: '',
        importMethod: null,
        requestStatus: null,
      },
      exchangeHistoryItems: [],
      // Incoming Report Requests (Exchange Requests tab)
      incomingRequestItems: [],
      incomingTableLoading: false,
      incomingTotalCount: 0,
      incomingReportOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      incomingFilters: {
        requestId: '',
        enterprise: '',
        product: null,
      },
      productOptions: [],
      exchangeHistoryLoaded: false,
      incomingRequestsLoaded: false,
    };
  },
  computed: {
    breadcrumbs() {
      return [
        { text: this.$t('dashboard'), disabled: true, to: '/' },
        { text: this.$t('dueDiligence.dueDiligence'), disabled: true, to: '#' },
        { text: this.$t('dueDiligence.createNewReport'), disabled: true, to: '#' },
      ];
    },
    importMethodOptions() {
      return [
        { text: this.$t('dueDiligence.all') || 'All', value: null },
        { text: this.$t('dueDiligence.accessCode'), value: 'access_code' },
        { text: this.$t('dueDiligence.internalReferenceNumber'), value: 'internal_ref' },
      ];
    },
    requestStatusOptions() {
      return [
        { text: this.$t('dueDiligence.all') || 'All', value: null },
        { text: this.$t('Approved') || 'Approved', value: 'approved' },
        { text: this.$t('Pending') || 'Pending', value: 'pending' },
        { text: this.$t('Rejected') || 'Rejected', value: 'rejected' },
      ];
    },
    tableHeaders() {
      return [
        { text: this.$t('dueDiligence.requestIdDate'), value: 'requestId', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.requestingEnterpriseNameId'), value: 'enterprise', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.importMethod'), value: 'importMethod', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.productQuantity'), value: 'productQuantity', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.dueDiligenceStatus'), value: 'dueDiligenceStatus', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.requestStatus'), value: 'requestStatus', sortable: true, align: 'start', class: 'black--text' },
      ];
    },
    incomingTableHeaders() {
      return [
        { text: this.$t('dueDiligence.requestIdDate'), value: 'requestId', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.requestingEnterpriseNameId'), value: 'enterprise', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.ddsInternalReferenceNo'), value: 'internalRefNo', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.productQuantity'), value: 'productQuantity', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.dueDiligenceStatus'), value: 'dueDiligenceStatus', sortable: true, align: 'start', class: 'black--text' },
        { text: this.$t('dueDiligence.requestStatus'), value: 'requestStatus', sortable: true, align: 'start', class: 'black--text' },
      ];
    },
    incomingTotalPages() {
      return Math.max(1, Math.ceil(this.incomingTotalCount / this.incomingReportOptions.itemsPerPage));
    },
    incomingFromToLabel() {
      const from = (this.incomingReportOptions.page - 1) * this.incomingReportOptions.itemsPerPage + 1;
      const to = (this.incomingReportOptions.page - 1) * this.incomingReportOptions.itemsPerPage + this.incomingRequestItems.length;
      return `${from} - ${to}`;
    },
    canAccessReport() {
      if (this.importMethod === 'access-code') return !!this.accessCode?.trim();
      if (this.importMethod === 'internal-ref') return !!this.internalRefNumber?.trim();
      return false;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.reportOptions.itemsPerPage));
    },
    fromToLabel() {
      const from = (this.reportOptions.page - 1) * this.reportOptions.itemsPerPage + 1;
      const to = (this.reportOptions.page - 1) * this.reportOptions.itemsPerPage + this.exchangeHistoryItems.length;
      return `${from} - ${to}`;
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 0 && !this.exchangeHistoryLoaded) {
        this.loadExchangeHistory();
      } else if (newTab === 1 && !this.incomingRequestsLoaded) {
        this.loadIncomingRequests();
        this.loadProductOptions();
      }
    },
  },
  created() {
    this.loadExchangeHistory();
  },
  methods: {
    accessTheReport() {
      if (this.importMethod === 'access-code' && this.accessCode?.trim()) {
        this.showImportDialog = true;
        this.importRunInBackground = false;
        this.importCancelled = false;
        this.accessReportLoading = true;
        this.doImportAccessCode(this.accessCode.trim());
        return;
      }
      if (this.importMethod === 'internal-ref' && this.internalRefNumber?.trim()) {
        this.showImportDialog = true;
        this.importRunInBackground = false;
        this.importCancelled = false;
        this.accessReportLoading = true;
        this.doImportInternalRef(this.internalRefNumber?.trim());
        return;
      }
    },
    onImportCancel() {
      this.importCancelled = true;
    },
    onImportRunInBackground() {
      this.importRunInBackground = true;
    },
    async doImportAccessCode(code) {
      try {
        const res = await DeforestationService.extractDdsReportWithAccessCode(code);
        const report = res?.data?.extractDdsReportWithAccessCode;
        this.showImportDialog = false;
        if (this.importCancelled) {
          this.loadExchangeHistory();
          return;
        }
        if (report?.id) {
          if (this.importRunInBackground) {
            this.$notify({
              text: this.$t('dueDiligence.checkExchangeHistory') || 'Import is running in the background. Check Exchange History for status.',
              type: 'success',
            });
            this.loadExchangeHistory();
          } else {
            this.$notify({
              text: this.$t('dueDiligence.accessReportSuccess') || 'Report linked successfully. You can now view it in Due Diligence Reports.',
              type: 'success',
            });
            this.accessCode = '';
            this.loadExchangeHistory();
            if (this.$user?.roles?.includes('kenya_ptsi') || this.$user?.roles?.includes('indonesia_ptsi')) {
              this.goToReport(report.id);
            } else {
              this.$router.push({ name: 'createDueDiligenceReports', params: { id: String(report.id) } });
            }
          }
        } else {
          this.$notify({
            text: this.$t('dueDiligence.accessReportError') || 'Could not link report.',
            type: 'warning',
          });
        }
      } catch (err) {
        this.showImportDialog = false;
        const msg = err?.response?.data?.errors?.[0]?.message
          || err?.response?.data?.message
          || (typeof err === 'string' ? err : err?.message)
          || this.$t('dueDiligence.accessReportError') || 'Invalid or expired access code.';
        this.$notify({
          text: typeof msg === 'string' ? msg : 'Invalid or expired access code.',
          type: 'error',
        });
      } finally {
        this.accessReportLoading = false;
      }
    },
    async doImportInternalRef(internalRef) {
      const inputRequest = {
        internal_ref_no: internalRef
      }
      try {
        const res = await DeforestationService.requestDDSReportFromInternalReference(inputRequest);
        const report = res?.data?.requestDDSReportFromInternalReference;
        this.showImportDialog = false;
        if (this.importCancelled) {
          this.loadIncomingRequests();
          return;
        }
        if (report?.id) {
          if (this.importRunInBackground) {
            this.$notify({
              text: this.$t('dueDiligence.checkExchangeHistory') || 'Import is running in the background. Check Exchange History for status.',
              type: 'success',
            });
          } else {
            this.$notify({
              text: this.$t('dueDiligence.requestSent') || 'Request sent. It will appear in Incoming Report Requests; approve it to access the report.',
              type: 'success',
            });
            this.internalRefNumber = '';
          }
          this.loadIncomingRequests();
          this.loadExchangeHistory();
        } else {
          this.$notify({
            text: this.$t('dueDiligence.accessReportError') || 'Could not request report.',
            type: 'warning',
          });
        }
      } catch (err) {
        this.showImportDialog = false;
        const msg = err?.response?.data?.errors?.[0]?.message
          || (typeof err === 'string' ? err : err?.message)
          || this.$t('dueDiligence.accessReportError') || 'Invalid internal reference number.';
        this.$notify({ text: typeof msg === 'string' ? msg : 'Invalid internal reference number.', type: 'error' });
      } finally {
        this.accessReportLoading = false;
      }
    },
    async loadExchangeHistory() {
      this.tableLoading = true;
      const pageQuery = {
        page: this.reportOptions.page,
        limit: this.reportOptions.itemsPerPage,
        listingType: 'import',
        requestStatus: this.filters.requestStatus,
        importMethod: this.filters.importMethod,
        requestId: this.filters.requestId,
        user: this.filters.enterprise,
      }
      try {
        const res = await DeforestationService.getDdsImportHistory(
          pageQuery
        );
        const data = res?.data?.getDdsImportHistory;
        const rows = data?.rows || [];
        this.totalCount = data?.totalCount ?? 0;
        this.exchangeHistoryItems = rows.map((row) => {
          const createdAt = row.createdAt ? new Date(row.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';
          const dr = row.diligenceReport;
          const quantityParts = [dr?.productNetMass, dr?.productVolume].filter(Boolean);
          const quantity = quantityParts.length ? quantityParts.join(' / ') : '—';
          const dueDiligenceStatus = dr?.status ?? null;
          return {
            id: row.id,
            requestId: String(row.id),
            requestDate: createdAt,
            enterpriseName: dr?.organization?.name ?? '—',
            enterpriseId: dr?.organization?.id ?? '—',
            importMethodLabel: row.access_code
              ? this.$t('dueDiligence.accessCode')
              : row.shared_by === null && row.requested_id
                ? this.$t('dueDiligence.internalReferenceNumber')
                : '—',
            accessCodeDisplay: row.access_code || dr?.internalReferenceNumber || '—',
            productName: dr?.product_detail?.name ?? '—',
            quantity,
            diligenceReportId: row.diligence_report_id,
            dueDiligenceStatus,
            requestStatus: row.request_status ?? 'pending',
            requestStatusLabel: this.getRequestStatusLabel(row.request_status),
          };
        });
        this.exchangeHistoryLoaded = true;
      } catch (err) {
        console.error('Load exchange history failed:', err);
        this.exchangeHistoryItems = [];
        this.totalCount = 0;
        this.$notify({
          text: this.$t('dueDiligence.exchangeHistoryLoadError') || 'Failed to load exchange history.',
          type: 'error',
        });
      } finally {
        this.tableLoading = false;
      }
    },
    getDueDiligenceStatusConfig(status) {
      if (!status) return { cssClass: 'gray-button', label: '—' };
      const s = status.toLowerCase();
      const map = {
        'pending':                   { cssClass: 'gray-button',   i18n: 'deforestation.pending' },
        'under review':              { cssClass: 'orange-button', i18n: 'deforestation.underReview' },
        'compliant':                 { cssClass: 'green-button',  i18n: 'deforestation.compliant' },
        'non-compliant':             { cssClass: 'red-button',    i18n: 'deforestation.nonCompliant' },
        'processing farms':          { cssClass: 'orange-button', i18n: 'deforestation.processingFarm' },
        'analyzing deforestation':   { cssClass: 'orange-button', i18n: 'deforestation.analyzeDeforestation' },
        'writing to blockchain':     { cssClass: 'orange-button', i18n: 'dueDiligence.writingToBlockchain' },
        'ready to proceed':          { cssClass: 'blue-button',   i18n: 'deforestation.readyToProceed' },
        'uploaded to eu portal':     { cssClass: 'blue-button',   i18n: 'deforestation.uploadedToEU' },
        'mitigation required':       { cssClass: 'green-button',  i18n: 'deforestation.mitigationRequired' },
      };
      const cfg = map[s];
      if (cfg) return { cssClass: cfg.cssClass, label: this.$t(cfg.i18n) || status };
      return { cssClass: 'gray-button', label: status };
    },
    getRequestStatusLabel(status) {
      if (!status) return 'N/A';
      const map = { approved: 'Approved', pending: 'Pending', request: 'Pending', rejected: 'Rejected' };
      const key = map[status] || status;
      return this.$t(key) || key;
    },
    isRequestApproved(status) {
      return String(status || '').toLowerCase() === 'approved';
    },
    getExchangeHistoryRowClass(item) {
      return this.isRequestApproved(item?.requestStatus) ? 'cursor-pointer' : '';
    },
    onExchangeHistoryRowClick(item) {
      if (!this.isRequestApproved(item?.requestStatus)) return;
      if (this.$user?.roles?.includes('kenya_ptsi') || this.$user?.roles?.includes('indonesia_ptsi')) {
        this.goToReport(item?.diligenceReportId);
      } else {
        this.$router.push({ name: 'createDueDiligenceReports', params: { id: String(item?.diligenceReportId) } });
      }
    },
    goToReport(diligenceReportId) {
      if (diligenceReportId) {
        this.$router.push({ name: 'viewSummaryReport', params: { id: String(diligenceReportId) } });
      }
    },
    reportPageChange(next) {
      if (next && this.reportOptions.page < this.totalPages) {
        this.reportOptions.page++;
        this.loadExchangeHistory();
      } else if (!next && this.reportOptions.page > 1) {
        this.reportOptions.page--;
        this.loadExchangeHistory();
      }
    },
    onFilterChange() {
      this.reportOptions.page = 1;
      this.loadExchangeHistory();
    },
    getRequestStatusBtnClass(status) {
      const map = {
        approved: 'green-button',
        pending: 'orange-button',
        request: 'orange-button',
        rejected: 'red-button',
      };
      return map[status] || 'gray-button';
    },
    getRequestStatusColor(status) {
      const colors = {
        approved: 'primary',
        pending: '#969696',
        request: '#969696',
        overdue: 'warning',
        rejected: 'error'
      };
      return colors[status] || 'grey';
    },
    copyAccessCode(item) {
      if (item.accessCodeDisplay) {
        navigator.clipboard?.writeText(item.accessCodeDisplay);
        this.$notify({ text: this.$t('dueDiligence.copied') || 'Copied', type: 'info' });
      }
    },
    async loadIncomingRequests() {
      this.incomingTableLoading = true;
      const pageQuery = {
        page: this.incomingReportOptions.page,
        limit: this.incomingReportOptions.itemsPerPage,
        listingType: 'request',
        user: this.incomingFilters.enterprise,
        productId: Number(this.incomingFilters.product),
      }
      try {
        const res = await DeforestationService.getDdsImportHistory(
          pageQuery
        );
        const data = res?.data?.getDdsImportHistory;
        const rows = data?.rows || [];
        this.incomingTotalCount = data?.totalCount ?? 0;
        this.incomingRequestItems = rows.map((row) => {
          const dr = row.diligenceReport;
          const createdAt = row.createdAt ? new Date(row.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';
          const quantityParts = [dr?.productNetMass, dr?.productVolume].filter(v => v && parseFloat(v) > 0);
          const quantity = quantityParts.length ? quantityParts.join(' / ') : '—';
          const dueDiligenceStatus = dr?.status ?? null;
          return {
            id: row.id,
            requestId: String(row.id),
            requestDate: createdAt,
            enterpriseName: dr?.organization?.name ?? '—',
            enterpriseId: dr?.organization?.id ?? '—',
            internalRefNo: dr?.internalReferenceNumber ?? '—',
            productName: dr?.product_detail?.name ?? '—',
            quantity,
            diligenceReportId: row.diligence_report_id,
            dueDiligenceStatus,
            requestStatus: row.request_status ?? 'request',
            requestStatusLabel: this.getRequestStatusLabel(row.request_status),
          };
        });
        this.incomingRequestsLoaded = true;
      } catch (err) {
        console.error('Load incoming requests failed:', err);
        this.incomingRequestItems = [];
        this.incomingTotalCount = 0;
        this.$notify({
          text: this.$t('dueDiligence.exchangeHistoryLoadError') || 'Failed to load incoming requests.',
          type: 'error',
        });
      } finally {
        this.incomingTableLoading = false;
      }
    },
    async loadProductOptions() {
      const res = await BlendService.getProducts({});
      const data = res?.data?.manageProduct;
      const rows = data?.rows || [];
      this.productOptions = rows.map((row) => {
        return { text: row.name, value: row.id };
      });
    },
    incomingPageChange(next) {
      if (next && this.incomingReportOptions.page < this.incomingTotalPages) {
        this.incomingReportOptions.page++;
        this.loadIncomingRequests();
      } else if (!next && this.incomingReportOptions.page > 1) {
        this.incomingReportOptions.page--;
        this.loadIncomingRequests();
      }
    },
    onIncomingFilterChange() {
      setTimeout(() => {
        this.incomingReportOptions.page = 1;
        this.loadIncomingRequests();
      }, 1000);
    },
    async handleApproveRequest(item) {
      this.startLoading();
      if (!item?.id || isNaN(item.id)) return;
      item.id = Number(item.id);
      try {
        await DeforestationService.approveDdsReportRequest(item.id);
        this.$notify({ text: this.$t('dueDiligence.requestApproved') || 'Request approved.', type: 'success' });
        this.loadIncomingRequests();
      } catch (err) {
        const msg = err?.response?.data?.errors?.[0]?.message || (typeof err === 'string' ? err : err?.message);
        this.$notify({ text: msg || this.$t('dueDiligence.requestApproveError') || 'Failed to approve.', type: 'error' });
      } finally {
        this.stopLoading();
      }
    },
    async handleRejectRequest(item) {
      this.startLoading();
      if (!item?.id || isNaN(item.id)) return;
      item.id = Number(item.id);
      try {
        await DeforestationService.rejectDdsReportRequest(item.id);
        this.$notify({ text: this.$t('dueDiligence.requestRejected') || 'Request rejected.', type: 'info' });
        this.loadIncomingRequests();
      } catch (err) {
        const msg = err?.response?.data?.errors?.[0]?.message || (typeof err === 'string' ? err : err?.message);
        this.$notify({ text: msg || this.$t('dueDiligence.requestRejectError') || 'Failed to reject.', type: 'error' });
      } finally {
        this.stopLoading();
      }
    },
  },
  mixins: [LoadingMixin],
};
</script>

<style scoped>
.exchange-tabs>>>.v-tab--active {
  font-weight: 600;
}

.theme--light.v-tabs-items {
  background-color: transparent !important;
}

.exchange-table>>>thead th {
  color: white !important;
  font-weight: 600;
}

.incoming-requests-table>>>thead th {
  color: white !important;
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}

.gray-button {
  background-color: #e8e8e8 !important;
  /* Green background */
  border: 2px solid #000000 !important;
  /* Red border */
  color: #000000 !important;
}
.blue-button {
  background-color: #d0e6fe !important;
  /* Green background */
  border: 2px solid #002287 !important;
  /* Red border */
  color: #002287 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

.green-button {
  background-color: #e8f5e9 !important;
  border: 2px solid #4caf50 !important;
  color: #4caf50 !important;
}

.red-button {
  background-color: #ffebee !important;
  border: 2px solid #f44336 !important;
  color: #f44336 !important;
}
</style>
